import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, CheckCircle, RefreshCw, MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithOtp } = useAuth();
  // Optional ?next=/some/path — used so deep-links (e.g. the "Log in to
  // write a review" CTA on TravelsProfile) return the user to where they
  // came from after a successful login. We only accept same-app paths
  // (must start with '/' and not '//') to avoid open-redirect.
  const rawNext = searchParams.get('next') || '';
  const nextPath = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '';
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'name'
  const [method, setMethod] = useState('email'); // 'email' | 'whatsapp'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);


  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    const isWhatsApp = method === 'whatsapp';
    if (isWhatsApp) {
      if (phone.replace(/\D/g, '').length < 10) {
        setError('Please enter a valid phone number');
        return;
      }
    } else if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const endpoint = isWhatsApp ? 'send-otp' : 'send-email-otp';
      const payload = isWhatsApp ? { phone: phone.trim() } : { email: email.trim() };
      const response = await fetch(`${API_BASE}/users/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setIsNewUser(data.isNewUser);
        setStep('otp');
        setResendTimer(30);
      } else {
        setError(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs[nextIndex]?.current?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          method === 'whatsapp'
            ? { phone: phone.trim(), otp: otpCode, name: name || undefined }
            : { email: email.trim(), otp: otpCode, name: name || undefined }
        )
      });

      const data = await response.json();

      if (data.success) {
        // Use AuthContext's loginWithOtp so the React state updates
        // immediately — direct localStorage writes don't trigger a re-render
        // of the navbar/Browse page.
        loginWithOtp(data.token, data.user);

        if (isNewUser && data.user.name.startsWith('User ')) {
          // First-time user with no real name — prompt for one before
          // sending them anywhere
          setStep('name');
        } else if (nextPath) {
          // Deep-link return path takes precedence over role-based default
          // (e.g. user came here from the "Log in to write a review" CTA)
          navigate(nextPath);
        } else {
          // Role-based redirect — owners/admins/managers/drivers land on
          // the dashboard, customers go to browse.
          const role = data.user.role;
          if (role === 'owner' || role === 'admin' || role === 'manager' || role === 'driver') {
            navigate('/dashboard');
          } else {
            navigate('/browse');
          }
        }
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setError('');
    setLoading(true);

    try {
      const endpoint = method === 'whatsapp' ? 'send-otp' : 'send-email-otp';
      const payload = method === 'whatsapp' ? { phone: phone.trim() } : { email: email.trim() };
      const response = await fetch(`${API_BASE}/users/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setResendTimer(30);
        setOtp(['', '', '', '', '', '']);
      } else {
        setError(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    navigate(nextPath || '/browse');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => {
            if (step === 'otp') {
              setStep('email');
              setOtp(['', '', '', '', '', '']);
            } else {
              navigate('/browse');
            }
          }}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          Back
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Email Step */}
          {step === 'email' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {method === 'whatsapp' ? (
                    <MessageCircle className="h-8 w-8 text-green-600" />
                  ) : (
                    <Mail className="h-8 w-8 text-blue-600" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Quick Login</h2>
                <p className="text-gray-500 mt-2">
                  {method === 'whatsapp'
                    ? "We'll send a code to your WhatsApp"
                    : 'Enter your email to book vehicles'}
                </p>
              </div>

              {/* Email / WhatsApp method toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                <button
                  type="button"
                  onClick={() => { setMethod('email'); setError(''); }}
                  className={`flex-1 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1 transition-colors ${method === 'email' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}
                >
                  <Mail className="h-4 w-4" /> Email
                </button>
                <button
                  type="button"
                  onClick={() => { setMethod('whatsapp'); setError(''); }}
                  className={`flex-1 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1 transition-colors ${method === 'whatsapp' ? 'bg-white shadow text-green-600' : 'text-gray-500'}`}
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </button>
              </div>

              <form onSubmit={handleSendOTP}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {method === 'whatsapp' ? 'Phone Number' : 'Email Address'}
                  </label>
                  <div className="relative">
                    {method === 'whatsapp' ? (
                      <>
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">+91</span>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Enter your phone number"
                          className="w-full pl-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-900"
                          autoFocus
                        />
                      </>
                    ) : (
                      <>
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                          autoFocus
                        />
                      </>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 animate-pulse font-medium">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || (method === 'whatsapp' ? phone.replace(/\D/g, '').length < 10 : (!email || !email.includes('@')))}
                  className={`w-full text-white py-3 rounded-lg font-medium flex items-center justify-center transition-colors ${method === 'whatsapp' ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-300' : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'}`}
                >
                  {loading ? (
                    'Sending OTP...'
                  ) : (
                    <>
                      {method === 'whatsapp' ? 'Get OTP on WhatsApp' : 'Get OTP'}
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </button>
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                By continuing, you agree to our Terms of Service
              </p>
            </>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Verify OTP</h2>
                <p className="text-gray-500 mt-2">
                  Enter the 6-digit code sent to<br />
                  <span className="font-medium text-gray-700">
                    {method === 'whatsapp' ? `+91 ${phone}` : email}
                  </span>
                </p>
              </div>


              <form onSubmit={handleVerifyOTP}>
                <div className="flex justify-center gap-3 mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={otpRefs[index]}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                      maxLength={6}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 animate-pulse font-medium">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || otp.join('').length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </button>
              </form>

              <div className="text-center mt-6">
                <p className="text-sm text-gray-500 mb-2">Didn't receive OTP?</p>
                <button
                  onClick={handleResendOTP}
                  disabled={resendTimer > 0}
                  className="text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center mx-auto disabled:text-gray-400"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </>
          )}

          {/* Name Step (for new users) */}
          {step === 'name' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Welcome!</h2>
                <p className="text-gray-500 mt-2">What should we call you?</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                  autoFocus
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 animate-pulse font-medium">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => navigate(nextPath || '/browse')}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleSaveName}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  Continue
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
